/**
 * "Push notifications on all your devices" (TRUS-697): the one switch that
 * stops every push - phones, tablets and browsers alike. It is the stored
 * `mobile_push_enabled` preference, which the server already applies to every
 * device; this makes that explicit. In-app notifications (the bell) are not
 * affected.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { usePushMasterSwitch } from '@/hooks/usePushMasterSwitch';

/**
 * Bring the switch into view (Settings -> Notifications tab), e.g. from the
 * "Send a test notification" result when push is off. The settings tabs are
 * uncontrolled, so this selects the tab through its trigger, then scrolls to
 * and focuses the switch.
 */
export function goToPushMasterSwitch(): void {
  if (typeof document === 'undefined') return;
  const focus = () => {
    const el = document.getElementById('push-master-switch');
    if (!el) return false;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      /* older engines */
    }
    el.focus();
    return true;
  };
  if (focus()) return;
  const tab = document.querySelector<HTMLElement>('[role="tab"][id$="-trigger-notifications"]');
  tab?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
  tab?.click();
  window.setTimeout(focus, 50);
}

export function PushMasterSetting() {
  const { t } = useAppTranslation();
  const { enabled, setEnabled } = usePushMasterSwitch();
  const [saving, setSaving] = useState(false);
  const on = enabled !== false;

  const change = async (next: boolean) => {
    setSaving(true);
    try {
      await setEnabled(next);
    } catch {
      toast.error(t('notifications.pushAll.saveFailed', 'Could not save your push setting. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-4 flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700"
      data-testid="push-master-setting"
    >
      <div className="min-w-0 flex-1">
        <label htmlFor="push-master-switch" className="text-base font-semibold">
          {t('notifications.pushAll.label', 'Push notifications on all your devices')}
        </label>
        <p className="text-sm text-gray-600 dark:text-gray-400" id="push-master-help" data-testid="push-master-help">
          {t(
            'notifications.pushAll.help',
            'Covers the WeaveIn app on your phones and tablets and every browser where you turned on notifications. When it is off, nothing is pushed to any of them; you still see notifications inside WeaveIn.',
          )}
        </p>
      </div>
      <Switch
        id="push-master-switch"
        checked={on}
        disabled={saving || enabled === undefined}
        onCheckedChange={(next) => void change(next)}
        aria-describedby="push-master-help"
        data-testid="push-master-switch"
      />
    </div>
  );
}

export default PushMasterSetting;
